/**
 * Flat area item used to build the area tree
 */
export class AreaItem {
  public id: number;
  public name: string = "";
  public parentId: number;

  constructor(item?: any) {
    // used for assign type to instance of object
    if (item) {
      Object.assign(this, item);
    }
  }
}

/**
 * Node of the merged area tree: areas with the same name under the same (merged) parent
 * are collapsed into one node, which keeps the ids of every instance it represents.
 */
export class AreaTreeNode {
  /** Stable identifier of the node, used for selection */
  public key: string = "";
  public name: string = "";
  /** Ids of the area instances merged into this node */
  public ids: Array<number> = [];
  public parentKey: string = null;
  /** TRUE for the fictitious root grouping several top-level areas */
  public isPlant: boolean = false;
  public children: Array<AreaTreeNode> = [];
  public expanded: boolean = false;

  constructor(node?: any) {
    // used for assign type to instance of object
    if (node) {
      Object.assign(this, node);
    }
  }
}

/**
 * Build and query the merged area tree
 */
export class AreaTree {
  /** Key of the fictitious "current plant" root node */
  public static readonly PLANT_KEY: string = "plant";

  /**
   * Build the merged area tree from the flat list.
   * Items whose parentId has no matching area (e.g. null or -1) are treated as roots.
   * When more than one root remains after merging, they are grouped under a fictitious root named plantName.
   * @param items flat list of areas
   * @param plantName name of the fictitious root
   */
  public static build(items: Array<AreaItem>, plantName: string): Array<AreaTreeNode> {
    const ids: Set<number> = new Set<number>(items.map((item) => item.id));
    const childrenByParent: Map<number, Array<AreaItem>> = new Map<number, Array<AreaItem>>();
    const rootItems: Array<AreaItem> = [];

    items.forEach((item) => {
      if (ids.has(item.parentId)) {
        if (!childrenByParent.has(item.parentId)) {
          childrenByParent.set(item.parentId, []);
        }
        childrenByParent.get(item.parentId).push(item);
      } else {
        rootItems.push(item);
      }
    });

    const roots: Array<AreaTreeNode> = AreaTree.mergeSiblings(rootItems, null, childrenByParent);

    if (roots.length <= 1) {
      return roots;
    }

    roots.forEach((root) => (root.parentKey = AreaTree.PLANT_KEY));

    return [
      new AreaTreeNode({
        key: AreaTree.PLANT_KEY,
        name: plantName,
        ids: roots.reduce<Array<number>>((acc, root) => acc.concat(root.ids), []),
        isPlant: true,
        children: roots,
        expanded: true,
      }),
    ];
  }

  /**
   * Return a deep copy of the tree, so that expanding/collapsing nodes does not alter the original.
   */
  public static clone(nodes: Array<AreaTreeNode>): Array<AreaTreeNode> {
    return nodes.map(
      (node) => new AreaTreeNode({ ...node, ids: [...node.ids], children: AreaTree.clone(node.children) })
    );
  }

  /**
   * Find a node by key in the tree.
   */
  public static find(nodes: Array<AreaTreeNode>, key: string): AreaTreeNode {
    for (const node of nodes) {
      if (node.key === key) {
        return node;
      }
      const found: AreaTreeNode = AreaTree.find(node.children, key);
      if (found) {
        return found;
      }
    }
    return null;
  }

  /**
   * Group sibling instances by name, sorted alphabetically, and merge their children recursively.
   */
  private static mergeSiblings(
    siblings: Array<AreaItem>,
    parentKey: string,
    childrenByParent: Map<number, Array<AreaItem>>
  ): Array<AreaTreeNode> {
    const groups: Map<string, Array<AreaItem>> = new Map<string, Array<AreaItem>>();

    siblings.forEach((item) => {
      const name: string = (item.name ?? "").trim();
      if (!groups.has(name)) {
        groups.set(name, []);
      }
      groups.get(name).push(item);
    });

    return Array.from(groups.entries())
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" }))
      .map(([, instances]) => {
        const key: string = instances.map((item) => item.id).join(",");
        const children: Array<AreaItem> = instances.reduce<Array<AreaItem>>(
          (acc, item) => acc.concat(childrenByParent.get(item.id) ?? []),
          []
        );

        return new AreaTreeNode({
          key,
          name: instances[0].name,
          ids: instances.map((item) => item.id),
          parentKey,
          children: AreaTree.mergeSiblings(children, key, childrenByParent),
        });
      });
  }
}
